import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import api from '../api/axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const token = searchParams.get('token');

        if (!token) {
          setStatus('error');
          setMessage(t.invalidOrExpiredToken);
          return;
        }

        // Call verify endpoint
        const response = await api.get(`/auth/verify-email/${token}`);

        // Success
        setStatus('success');
        setMessage(response.data.message || t.verificationSuccess);

        toast.success(t.emailVerified);

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);

      } catch (error) {
        setStatus('error');

        if (error.response?.status === 400) {
          setMessage(error.response.data.detail || t.invalidOrExpiredToken);
        } else {
          setMessage(error.response?.data?.detail || t.verificationFailed);
        }

        toast.error(t.verificationFailed);
      }
    };

    verifyToken();
  }, [searchParams, language, navigate]);

  return (
    <>
      <Helmet>
        <title>Verify Email - FeedPanels</title>
        <meta name="description" content="Confirm your email address for FeedPanels. Click the link we sent to activate your account and start collecting feedback." />
        <link rel="canonical" href="https://feedpanels.com/verify-email" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-800 border-slate-700">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {status === 'loading' && (
              <Loader2 className="w-12 h-12 text-cyan-500 animate-spin" />
            )}
            {status === 'success' && (
              <CheckCircle2 className="w-12 h-12 text-green-500" />
            )}
            {status === 'error' && (
              <AlertCircle className="w-12 h-12 text-red-500" />
            )}
          </div>

          <h1 className="text-white text-2xl font-semibold">
            {status === 'loading' && t.verifying}
            {status === 'success' && t.emailVerified}
            {status === 'error' && t.verificationError}
          </h1>

          <CardDescription className={`mt-2 ${
            status === 'success' ? 'text-green-400' : 'text-red-400'
          }`}>
            {message}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <div className="space-y-4">
            {status === 'loading' && (
              <p className="text-center text-slate-400">
                {t.verificationInProgress}
              </p>
            )}

            {status === 'success' && (
              <div className="bg-green-900 bg-opacity-20 border border-green-600 rounded-lg p-4 text-center">
                <p className="text-green-400 text-sm">
                  {t.redirectingToLogin}
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="space-y-4">
                <div className="bg-red-900 bg-opacity-20 border border-red-600 rounded-lg p-4">
                  <p className="text-red-400 text-sm">
                    {t.verificationFailed}
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => navigate('/register')}
                    className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
                  >
                    {t.registerAgain}
                  </Button>

                  <Button
                    onClick={() => navigate('/login')}
                    variant="outline"
                    className="flex-1 border-cyan-600 text-cyan-400 hover:bg-cyan-900 hover:bg-opacity-20"
                  >
                    {t.signIn}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
    </>
  );
};
